import React from "react";

/**
 * Privacy Policy page for inkstead.in
 *
 * Usage:
 *   1. Save this file as src/pages/PrivacyPolicy.jsx
 *   2. Add a route in your router, e.g. (React Router v6):
 *        import PrivacyPolicy from "./pages/PrivacyPolicy";
 *        <Route path="/privacy" element={<PrivacyPolicy />} />
 *   3. Link to it from your footer: <Link to="/privacy">Privacy Policy</Link>
 *
 * IMPORTANT: The bracketed [PLACEHOLDER] values below need to be filled in
 * with your real details. This is a template based on common practices for
 * a platform connecting clients with tattoo artists — it is not legal advice.
 * Have a lawyer review it before relying on it, especially the India DPDP Act
 * section, before you launch.
 */

const PrivacyPolicy = () => {
  const lastUpdated = "October 5, 2026";

  return (
    <div className="min-h-screen bg-white text-gray-800">
      <div className="max-w-3xl mx-auto px-6 py-16">
        <h1 className="text-3xl md:text-4xl font-bold mb-2">Privacy Policy</h1>
        <p className="text-sm text-gray-500 mb-10">Last updated: {lastUpdated}</p>

        <Section title="1. Introduction">
          <p>
            Inkstead ("Inkstead," "we," "us," or "our") operates inkstead.in
            (the "Platform"), connecting clients with tattoo artists and
            studios. This Privacy Policy explains what personal data we
            collect, how we use it, and the choices you have.
          </p>
          <p>
            By using the Platform, you agree to the collection and use of
            information in accordance with this policy. If you do not agree,
            please do not use the Platform.
          </p>
        </Section>

        <Section title="2. Information We Collect">
          <h3 className="font-semibold mt-4 mb-1">a. Information you provide directly</h3>
          <ul className="list-disc pl-6 space-y-1">
            <li>Account details: name, email address, phone number, password</li>
            <li>Profile information: profile photo, bio, location</li>
            <li>
              Artist-specific information (if you register as an artist):
              portfolio images, studio name, pricing, availability, certifications
            </li>
            <li>Content you post: reviews, comments, community posts, messages</li>
            <li>Booking details: appointment requests, preferences, correspondence with artists</li>
            <li>Support requests and correspondence with us</li>
          </ul>

          <h3 className="font-semibold mt-4 mb-1">b. Information collected automatically</h3>
          <ul className="list-disc pl-6 space-y-1">
            <li>Device information: browser type, operating system, device identifiers</li>
            <li>Usage data: pages visited, features used, time spent, referring URLs</li>
            <li>Approximate location (derived from IP address)</li>
            <li>Cookies and similar technologies (see Section 6)</li>
          </ul>

          <h3 className="font-semibold mt-4 mb-1">c. Information from third parties</h3>
          <ul className="list-disc pl-6 space-y-1">
            <li>If you sign in via a third-party service (e.g. Google), we receive basic profile information from that provider</li>
          </ul>
        </Section>

        <Section title="3. How We Use Your Information">
          <ul className="list-disc pl-6 space-y-1">
            <li>To create and manage your account</li>
            <li>To connect clients with artists and facilitate bookings</li>
            <li>To display artist portfolios and profiles to prospective clients</li>
            <li>To enable community features such as posts and comments</li>
            <li>To send service-related communications (confirmations, reminders, updates)</li>
            <li>To send marketing communications, where you've opted in</li>
            <li>To improve, secure, and troubleshoot the Platform</li>
            <li>To comply with legal obligations and enforce our terms</li>
          </ul>
        </Section>

        <Section title="4. How We Share Information">
          <p>We do not sell your personal data. We may share information with:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <span className="font-medium">Artists and clients,</span> as
              needed to facilitate a booking (e.g. an artist sees a client's
              name and booking request)
            </li>
            <li>
              <span className="font-medium">Service providers</span> who
              perform functions on our behalf, such as hosting, payment
              processing, analytics, and customer support
            </li>
            <li>
              <span className="font-medium">Legal and safety purposes,</span>{" "}
              where required by law, to enforce our terms, or to protect the
              rights, property, or safety of Inkstead, our users, or the
              public
            </li>
            <li>
              <span className="font-medium">Business transfers,</span> in
              connection with a merger, acquisition, or sale of assets
            </li>
          </ul>
        </Section>

        <Section title="5. Cookies and Tracking">
          <p>
            We use cookies and similar technologies to keep you signed in,
            remember preferences, and understand how the Platform is used.
            You can control cookies through your browser settings; disabling
            them may affect some features.
          </p>
          <p>We may use the following categories of cookies:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Essential cookies (required for login and core functionality)</li>
            <li>Analytics cookies (to understand usage patterns)</li>
            <li>Preference cookies (to remember your settings)</li>
          </ul>
        </Section>

        <Section title="6. Data Retention">
          <p>
            We retain personal data for as long as your account is active or
            as needed to provide the Platform, comply with legal obligations,
            resolve disputes, and enforce our agreements. You may request
            deletion of your account and associated data at any time, subject
            to legal retention requirements (e.g. transaction records).
          </p>
        </Section>

        <Section title="7. Data Security">
          <p>
            We use reasonable technical and organizational measures to
            protect your personal data. However, no method of transmission
            or storage is completely secure, and we cannot guarantee
            absolute security.
          </p>
        </Section>

        <Section title="8. Your Rights and Choices">
          <p>Depending on your location, you may have the right to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Access the personal data we hold about you</li>
            <li>Correct inaccurate or incomplete data</li>
            <li>Request deletion of your data</li>
            <li>Withdraw consent for marketing communications</li>
            <li>Request a copy of your data in a portable format</li>
            <li>Lodge a complaint with a data protection authority</li>
          </ul>
          <p className="mt-2">
            To exercise these rights, contact us at [PRIVACY CONTACT EMAIL].
          </p>
        </Section>

        <Section title="9. Users in India (DPDP Act, 2023)">
          <p>
            If you are located in India, we process your personal data as a
            "Data Fiduciary" under the Digital Personal Data Protection Act,
            2023. We process data based on your consent or other grounds
            permitted under the Act, and you have the right to access,
            correct, and erase your personal data, and to withdraw consent
            at any time. To exercise these rights or raise a grievance,
            contact our Grievance Officer:
          </p>
          <p className="mt-2">
            Tapendra Joshi
            <br />
            Email: support@inkstead.in
            <br />
            Address: India
          </p>
        </Section>

        <Section title="10. International Users">
          <p>
            If you access the Platform from outside India, your information
            may be transferred to and processed in India or other countries
            where we or our service providers operate. By using the
            Platform, you consent to this transfer.
          </p>
        </Section>

        <Section title="11. Third-Party Links">
          <p>
            The Platform may contain links to third-party websites or
            services (e.g. an artist's Instagram profile). We are not
            responsible for the privacy practices of those third parties.
          </p>
        </Section>

        <Section title="12. Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time. We will
            post the updated version on this page with a new "Last updated"
            date. Material changes may also be communicated via email or an
            in-app notice.
          </p>
        </Section>

        <Section title="13. Contact Us">
          <p>
            If you have questions about this Privacy Policy or how we handle
            your data, contact us at:
          </p>
          <p className="mt-2">
            Email: support@inkstead.in
            <br />
            Address: India
          </p>
        </Section>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-xl font-semibold mb-3">{title}</h2>
    <div className="space-y-3 text-gray-700 leading-relaxed">{children}</div>
  </section>
);

export default PrivacyPolicy;
